import {
  LendingPaymentSource,
  RouteNavigationAction,
  SubmitManualAdvancePaymentResultTypenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import ConfirmPayment from 'features/advance/components/ConfirmPayment/ConfirmPayment';
import {
  mapLendingPaymentSourceToPaymentSourceInput,
  mapLendingPaymentSourceToString,
} from 'features/advance/helpers/Advance.helpers';
import useConfirmAdvanceRepayment from 'features/advance/hooks/useConfirmAdvanceRepayment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

const AdvanceConfirmPaymentScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'advanceConfirmPaymentStrings',
  });
  const [isMounted, setIsMounted] = useState(false);

  const { bottom } = useSafeAreaInsets();
  const dismissToTab = useDismissToRootTab();

  const {
    id,
    repayAmount,
    accountName,
    pendingAmount,
    sourceAccountId,
    advanceStatus,
    advanceId,
    paymentSource,
  } = useLocalSearchParams<{
    id: string;
    repayAmount: string;
    accountName: string;
    sourceAccountId: string;
    pendingAmount: string;
    advanceStatus: string;
    advanceId: string;
    paymentSource: LendingPaymentSource;
  }>();

  const hasUrlParams =
    !!id &&
    !!repayAmount &&
    !!accountName &&
    !!sourceAccountId &&
    !!pendingAmount &&
    !!advanceStatus &&
    !!advanceId;

  const paymentMethod = paymentSource
    ? mapLendingPaymentSourceToString(paymentSource)
    : '';

  const institutionName =
    paymentSource === LendingPaymentSource.InternalDda ||
    paymentSource === LendingPaymentSource.InternalSavings
      ? 'Varo'
      : (accountName ?? '');

  useViewDidFocusScreenAnalyticsEvent({
    key: '/accounts/varo-advance/confirm-payment',
    customAttributes: {
      payment_method: paymentMethod,
      institution_name: institutionName,
    },
  });

  const externalAchPayment = paymentSource === LendingPaymentSource.ExternalAch;
  const externalDebitCardPayment =
    paymentSource === LendingPaymentSource.ExternalDebitCard;
  const todaysDate = new Date().toLocaleDateString();

  // Check whether the component is fully mounted
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const {
    executeConfirmRepaymentMutation,
    fetching,
    error: paymentError,
  } = useConfirmAdvanceRepayment();

  if (!hasUrlParams && isMounted) {
    return <Redirect href="/varo-advance" />;
  }

  const confirmPayment = async () => {
    const result = await executeConfirmRepaymentMutation({
      idempotencyId: getUuid(),
      input: {
        advanceId: advanceId,
        paymentAccountId: sourceAccountId,
        amount: {
          amount: repayAmount,
          currency: 'USD',
        },
        paymentSource:
          mapLendingPaymentSourceToPaymentSourceInput(paymentSource),
      },
    });

    const submitManualAdvancePaymentResult =
      result?.data?.submitManualAdvancePayment;

    if (
      submitManualAdvancePaymentResult?.__typename ===
      SubmitManualAdvancePaymentResultTypenames.SubmitManualAdvancePaymentSuccess
    ) {
      router.dismissAll();
      router.navigate({
        pathname: '/accounts/varo-advance/confirm-payment/success',
        params: {
          id,
          title: submitManualAdvancePaymentResult?.title,
          heroText: submitManualAdvancePaymentResult?.heroText,
          message: submitManualAdvancePaymentResult?.message,
          infoMessage: submitManualAdvancePaymentResult?.infoMessage,
          pendingAmount,
          advanceStatus,
          paymentSource,
          institutionName: institutionName ?? '',
        },
      });
    }
  };

  const handleClose = (): void => {
    const routeNavigationAction =
      paymentError?.action?.buttonOnClick?.__typename ===
      'RouteNavigationAction'
        ? (paymentError.action.buttonOnClick as RouteNavigationAction)
        : undefined;

    if (routeNavigationAction?.route) {
      router.navigate(routeNavigationAction.route as Href);
      return;
    }

    dismissToTab(Tabs.HOME);
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: !paymentError,
          headerBackVisible: !paymentError,
          title: translatedText('confirmPayment'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ConfirmPayment repayAmount={repayAmount} accountName={accountName} />

      {externalAchPayment || externalDebitCardPayment ? (
        <View style={styles.disclaimerContainer}>
          <BodyXSText style={styles.disclaimer}>
            {externalAchPayment
              ? translatedText('achDisclaimer', {
                  repayAmount,
                  accountName,
                  todaysDate,
                })
              : null}
            {externalDebitCardPayment
              ? translatedText('debitCardDisclaimer', {
                  repayAmount,
                  accountName,
                  todaysDate,
                })
              : null}
          </BodyXSText>
        </View>
      ) : null}

      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('confirmPayment')}
          onPress={confirmPayment}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
          analyticsId="confirmPaymentClicked"
          customAttributes={{
            amount: repayAmount,
            payment_method: paymentMethod,
            institution_name: institutionName,
            payment_type: pendingAmount === '0' ? 'full' : 'partial',
          }}
          isLoading={fetching}
        />
      </View>
      {paymentError ? (
        <ScreenTransitionOverlay
          isLoading={false}
          LoadingComponent={undefined}
          ErrorComponent={
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={paymentError.title}
              messageBody={paymentError.message}
              primaryButton={{
                title:
                  paymentError.action?.buttonText ??
                  translatedText('errorButton'),
                onPress: handleClose,
                analytics: {
                  analyticsId: 'VAConfirmPaymentErrorScreenButton',
                  customAttributes: {
                    error_name: paymentError.message,
                    step_name: 'va_repayment_confirmation',
                  },
                },
              }}
              screenAnalyticsEventKey="accounts/varo-advance/confirm-payment/errorScreen"
            />
          }
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingBottom: theme.spacing.l,
  },
  disclaimer: {
    color: theme.color.text.secondary,
  },
  disclaimerContainer: {
    padding: theme.spacing.m,
  },
});

export default AdvanceConfirmPaymentScreen;
